import FormControl from '@mui/material/FormControl'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import moment from 'moment'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { withStyles } from '~/themes/useStyles'
import { COLORS } from '~/themes/common'
import { convertH2M, USER_DEVICE_ZONE } from '~/utils/datetime'

const BootstrapInput = withStyles((theme) => ({
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase)

const themeStyle = () => ({
  root: {},
  margin: {
    width: '100%',
    textAlign: 'left',
    '& .MuiSelect-select.Mui-disabled': {
      backgroundColor: COLORS.gray.disableGray2,
      color: COLORS.palette.black,
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '& .MuiInputBase-root.Mui-disabled': {
      '& svg': {
        color: 'rgba(0, 0, 0, 0.26)',
      },
    },
  },
  wrapMenu: {
    padding: 0,
    '& ul.MuiList-root.MuiMenu-list': {
      padding: 0,
      paddingRight: 0,
    },
    '&::-webkit-scrollbar': {
      width: 5,
      height: 5,
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: 10,
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 10,
      backgroundColor: '#b4bfd9',
    },
  },
  menuList: {
    maxHeight: '50vh',
    '& li': {
      font: 'normal normal normal 14px/17px Rubik',
      letterSpacing: '0px',
      color: '#292929',
      paddingBottom: 10,
      paddingTop: 10,
      padding: '0 39px 20px 25px',
      paddingLeft: 24,
      display: 'flex',
      justifyContent: 'center',
    },
  },
  selectWrapper: {
    '& > div': {
      height: 16,
      padding: '3px 16px 4px 14px',
      fontSize: 14,
      minHeight: 'unset',
      border: '1px solid #B4BFD9',
      backgroundColor: '#EDF1FB',
    },
  },
  selectErrorWrapper: {
    '& > div': {
      height: 16,
      padding: '3px 16px 4px 14px',
      fontSize: 14,
      minHeight: 'unset',
      border: '1px solid #F64066',
      backgroundColor: '#EDF1FB',
    },
    '& > svg': {
      display: 'none',
    },
    '& > div:focus': {
      border: '1px solid #F64066',
      boxShadow: 'unset',
      borderColor: '#F64066',
    },
  },
  arrowDown: {
    position: 'absolute',
    right: 4,
    top: 6,
  },
  timeItem: {
    width: '100%',
  },
  placeholder: {
    color: COLORS.gray.textGray2,
    opacity: 0.42,
  },
})

const iconStyles = {
  selectIcon: {
    color: '#2933C5',
    right: 4,
    top: 4,
  },
}
const CustomExpandMore = withStyles(iconStyles)(
  ({ className, classes, ...rest }) => (
    <ExpandMoreIcon {...rest} className={clsx(className, classes.selectIcon)} />
  ),
)

const TimeSelect = ({
  classes,
  form,
  placeholder,
  disabled,
  helperText,
  error,
  hasPeriods,
  onChange,
  value,
  realTimeWithPresent,
  timezone = USER_DEVICE_ZONE,
  className,
  timeGap,
  inputFormat,
  name,
  timeOptionsRange,
  dataTestId,
  paperDataTestId,
  ...props
}) => {
  const times = useMemo(() => {
    let loop = 0
    let hour = 0
    const arrSelectTimes = []
    const timeOptionsRangeStartMin = timeOptionsRange.start
      ? convertH2M(moment(timeOptionsRange.start, 'hh:mm A').format('HH:mm'))
      : null
    const timeOptionsRangeEndMin = timeOptionsRange.end
      ? convertH2M(moment(timeOptionsRange.end, 'hh:mm A').format('HH:mm'))
      : null

    for (let i = 0; i <= (24 * 60) / timeGap; i += 1) {
      let label = `${hour.toLocaleString().length === 1 ? `0${hour}` : hour} : ${(loop * timeGap).toLocaleString().length === 1 ? `0${loop * timeGap}` : loop * timeGap}`
      if (hour * 60 + loop * timeGap === 1440) {
        label = '23:59'
      }
      arrSelectTimes.push({
        value: moment(label, 'HH:mm').format('hh:mm A'),
        value24h: moment(label, 'HH:mm').format('HH:mm'),
        label: moment(label, 'HH:mm').format('hh:mm A'),
      })
      loop += 1
      if (loop === 60 / timeGap) {
        hour += 1
      }
      if (loop === 60 / timeGap) {
        loop = 0
      }
    }
    return arrSelectTimes
      .filter((i) => {
        if (timeOptionsRangeStartMin) {
          return convertH2M(i.value24h) > timeOptionsRangeStartMin
        }
        if (timeOptionsRangeEndMin) {
          return convertH2M(i.value24h) < timeOptionsRangeEndMin
        }
        if (realTimeWithPresent) {
          const convertedValue = moment(
            moment(i.value, 'hh:mm A').format('HH:mm'),
            'HH:mm',
          )
          const h = convertedValue.hour()
          const m = convertedValue.minute()
          const now = moment().tz(timezone)
          return h > now.hour() || (h === now.hour() && m > now.minute())
        }
        return true
      })
      .map((item) => ({
        ...item,
        label: moment(item.label, 'hh:mm A').format(
          hasPeriods ? 'hh:mm A' : 'HH:mm A',
        ),
      }))
  }, [timeGap, hasPeriods, realTimeWithPresent, timeOptionsRange])

  const handleChangeValue = (e) => {
    if (onChange) {
      onChange(e)
    } else {
      form.setFieldValue(name, e.target.value)
    }
  }

  return (
    <div
      className={clsx(classes.root, {
        [className]: !!className,
      })}
      id="time-picker-select"
    >
      <FormControl className={classes.margin}>
        <Select
          labelId="customized-select-label"
          {...(placeholder && !value
            ? {
                renderValue: () => (
                  <div className={classes.placeholder}>{placeholder}</div>
                ),
                displayEmpty: true,
              }
            : {
                renderValue: (val) => val,
              })}
          onChange={handleChangeValue}
          input={<BootstrapInput data-testid={dataTestId} {...props} />}
          disabled={disabled}
          placeholder={placeholder}
          value={
            value
              ? moment(value, inputFormat).format(
                  hasPeriods ? 'hh:mm A' : 'HH:mm A',
                )
              : value || ''
          }
          MenuProps={{
            classes: { paper: classes.wrapMenu, list: classes.menuList },
            MenuListProps: {
              'data-testid': paperDataTestId,
            },
          }}
          // endAdornment={<span className={classes.arrowDown}><KeyboardArrowDown color="primary" /></span>}
          IconComponent={CustomExpandMore}
          className={clsx(
            'customized-select',
            error ? classes.selectErrorWrapper : classes.selectWrapper,
          )}
        >
          <MenuItem value="Time" disabled>
            {placeholder}
          </MenuItem>
          {times.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              <span className={classes.timeItem}>
                {hasPeriods ? item.label : item.label.split(' ')[0]}
              </span>
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <Box>
            <p
              style={{
                color: '#F64066',
                fontSize: 12,
                marginTop: 4,
                marginLeft: 3,
              }}
            >
              {helperText}
            </p>
          </Box>
        )}
      </FormControl>
    </div>
  )
}

TimeSelect.defaultProps = {
  placeholder: 'Time',
  disabled: false,
  helperText: '',
  error: false,
  hasPeriods: true,
  onChange: null,
  value: '',
  form: {},
  realTimeWithPresent: false,
  timezone: USER_DEVICE_ZONE,
  className: '',
  timeGap: 15,
  inputFormat: 'HH:mm',
  name: '',
  timeOptionsRange: {
    start: null,
    end: null,
  },
  dataTestId: '',
  paperDataTestId: '',
}
TimeSelect.propTypes = {
  classes: PropTypes.shape().isRequired,
  form: PropTypes.shape(),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  hasPeriods: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  realTimeWithPresent: PropTypes.bool,
  timezone: PropTypes.string,
  className: PropTypes.string,
  timeGap: PropTypes.oneOf([10, 15, 20, 30]),
  inputFormat: PropTypes.string,
  name: PropTypes.string,
  timeOptionsRange: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  dataTestId: PropTypes.string,
  paperDataTestId: PropTypes.string,
}

export default withStyles(themeStyle)(TimeSelect)
